import React from "react";
import { waLink } from "@/lib/brand";
import SaveNumberLink from "@/components/SaveNumberLink";

const ZONES = [
  {
    slug: "gawa",
    name: "From the Gawa",
    bengali: "গাওয়া",
    desc: "The freshwater ponds of the farm. Rui, catla, tilapia raised in working water.",
    tone: "block-placeholder--river",
  },
  {
    slug: "brackish",
    name: "Brackish",
    bengali: "ভাের",
    desc: "The brackish system where mud crab and prawn are drawn when the kitchen needs them.",
    tone: "block-placeholder",
  },
  {
    slug: "field",
    name: "Field",
    bengali: "ক্ষেত",
    desc: "Tomato, aubergine, gourd, leaf. Turned by hand. What is ready today.",
    tone: "block-placeholder--dark",
  },
  {
    slug: "grove",
    name: "Grove",
    bengali: "ফলবাগান",
    desc: "Two hundred fruit trees. Mango, jackfruit, guava, custard-apple. Whatever the orchard held.",
    tone: "block-placeholder--cream",
  },
  {
    slug: "hearth",
    name: "Hearth",
    bengali: "উনুন",
    desc: "Clay pots since morning. Slow onion, rendered fat, whole spice, patience.",
    tone: "block-placeholder--clay",
  },
  {
    slug: "daily-read",
    name: "The Daily Read",
    bengali: "আজকের কথা",
    desc: "Written each morning at the farm gate. Read aloud at the table before the meal begins.",
    tone: "block-placeholder--cream",
  },
];

const ACTS = [
  {
    id: "act-1",
    roman: "ACT I",
    bengali: "প্রকৃতি",
    subtitle: "Nature's Opening",
    heading: "To Begin",
    lede:
      "Three things arrive together. They are the farm's morning compressed into three bites. Nothing here has been cooked at length. Eat them in any order.",
    label: "BHUMIKA · THREE OPENING BITES",
    dishes: [
      {
        slug: "haaser-dim-bhapa",
        name: "Haas'er Dim Bhapa",
        detail:
          "Duck egg from the Haas Ghar · mustard and coconut paste inside · steamed in shell · banana leaf seal.",
        note:
          "The shell arrives on an earthen dish. You tap it open yourself. The paste was put inside before the egg was sealed and it has been cooking inside the shell the whole time.",
      },
      {
        slug: "shukto-posto-til",
        name: "Shukto Posto Til",
        detail:
          "Farm rice cracker · pressed with black sesame · dried shrimp powder · poppy paste.",
        note:
          "One bite. The rice is from the farm. The shrimp was dried on the property. The sesame came from the Upper Field last season.",
      },
      {
        slug: "kancha-aam-noon-morich",
        name: "Kancha Aam Noon Morich",
        detail:
          "Raw mango from the phalbagan · black salt · crushed green peppercorn · nothing else.",
        note:
          "A single slice. The acid that opens everything that follows. The mango was on the tree this morning.",
      },
    ],
  },
  {
    id: "act-2a",
    roman: "ACT II",
    bengali: "নদী",
    subtitle: "From the Gawa to the River",
    heading: "The Water Deepens",
    lede:
      "Seven courses moving outward from the freshwater pond to the tidal river. The water changes with each course. The fish change with the water.",
    dishes: [
      {
        slug: "chingri-nodir-nun",
        name: "Chingri aar Nodir Nun",
        detail:
          "River prawn · cold-poached in tidal water · river salt · lime-leaf oil.",
        note:
          "Poached in water drawn from the Matla this morning. Dressed only with salt from the same river and oil pressed from the farm's lime leaves. The simplest course on the menu. It is intended to taste like the river.",
      },
      {
        slug: "topshe-kalo-jeera",
        name: "Topshe, Kalo Jeera Tel",
        detail:
          "Mango fish from the tidal river · mustard oil · nigella seed · barely touched.",
        note:
          "The topshe is the most delicate river fish in Bengal and one of the least known outside it. Fried lightly in mustard oil with nigella. Three pieces. Nothing else on the plate — anything added would be interference.",
        season: "SEASONAL · WINTER RUN, OCTOBER THROUGH FEBRUARY",
      },
      {
        slug: "parshe-shorshe-paata",
        name: "Parshe, Shorshe Paata",
        detail:
          "Mullet from the tidal khal · green mustard paste · turmeric · single green chilli · steamed in mustard leaf.",
        note:
          "The mustard leaf is the vessel, the seal, and part of the flavour. The packet arrives at the table folded. The guest opens it. The steam that escapes when it opens is the first thing you smell.",
      },
      {
        slug: "daab-kankra",
        name: "Daab Kankra",
        detail:
          "Mud crab from the Bhaer · steamed in the farm's own coconut water · crab-coral butter · lime-leaf oil · served in the shell.",
        note:
          "The crab has been in the Bhaer since the last tide. The coconut came from the Palm Line this morning. They have not met before this moment. The coral butter is the roe emulsified into white butter — it turns the shell gold.",
      },
      {
        slug: "rui-shorshe",
        name: "Rui Shorshe",
        detail:
          "East Gawa rui · stone-mustard paste · nigella · single green chilli whole · hand-pounded rice.",
        note:
          "The recipe belongs to the woman who tends the east pond. The chilli goes in whole and does not come out. The rice is pounded by hand in the farm kitchen before service.",
      },
      {
        slug: "bhekti-golpata",
        name: "Bhekti, Golpata Dhoa",
        detail:
          "Barramundi from the estuarine river · smoked over burning golpata leaves · skin crisped · stone mustard · farm vegetable achaar.",
        note:
          "The golpata that thatched your roof is the golpata that smoked this fish. The same palm serves two purposes on this property. The smoke enters the flesh without overwhelming it — golpata burns cooler than most woods and the fish stays its own thing.",
      },
      {
        slug: "ilish-shorshe-kola-pata",
        name: "Ilish, Shorshe, Kola Pata",
        pin: "The River's Offering",
        detail:
          "Sundarbans Hilsa · stone mustard · turmeric · green chilli · steamed in banana leaf.",
        note:
          "The Hilsa migrates from the Bay of Bengal into the tidal rivers to spawn. It is most prized at the moment of transition, when the flesh carries both salt water and fresh simultaneously. This is that fish, at that moment, prepared only with mustard and the leaf of the banana grove. The banana leaf arrives folded at the table. Your host opens it. That is the entire service of this course.",
        season:
          "SEASONAL · MONSOON RUN JUNE–SEPTEMBER · WINTER RUN NOVEMBER–JANUARY · WHEN THE ILISH IS NOT IN THE RIVER THIS COURSE DOES NOT APPEAR. WE DO NOT SUBSTITUTE.",
      },
    ],
  },
  {
    id: "act-3",
    roman: "ACT III",
    bengali: "আগুন",
    subtitle: "What has been cooking since this afternoon",
    heading: "The Hearth Responds",
    lede:
      "The farm's land animals. Each has been in clay or over fire since the afternoon. They will not be hurried and have not been.",
    dishes: [
      {
        slug: "haas-shorshe-kancha-lanka",
        name: "Haas, Shorshe, Kancha Lanka",
        detail:
          "Farm duck · slow-cooked three hours in clay · mustard paste · single whole green chilli placed on top to finish in steam.",
        note:
          "The duck knows the gawa. It went there this morning. This is the other side of that story. Served with a small portion of hand-pounded rice pressed into a cylinder — the rice to carry the pot liquid, which has become thick and gold.",
      },
      {
        slug: "murgi-dhoan-shorshe-bori",
        name: "Murgi, Dhoan, Shorshe, Bori",
        detail:
          "Free-range hen from the Murgi Bari · older bird · smoked mustard paste · sun-dried lentil dumplings · clay vessel.",
        note:
          "An older bird, not a young broiler, because the muscle density and fat of a bird that has ranged the grove for months rewards slow cooking in a way a younger bird cannot. The bori have been drying in the sun since yesterday. They absorb the cooking liquid and become something between dumpling and sauce.",
      },
      {
        slug: "patha-maatir-haari",
        name: "Patha, Maatir Haari",
        detail:
          "Goat shank and shoulder · sealed clay pot since morning · whole spice · slow onion · rendered fat from yesterday's cooking.",
        note:
          "Eight hours in a sealed pot. The pot is brought to the table and opened there. The seal breaks with a sound. What comes out has been building since this morning. With it: a small amount of the phalbagan's most acidic current pickle, to cut through the fat, which needs cutting.",
      },
    ],
  },
  {
    id: "interlude",
    roman: "INTERLUDE",
    bengali: "নবান্ন",
    subtitle: "The new rice · The return",
    heading: "Between the Hearth and the Sweet",
    lede: "",
    dishes: [
      {
        slug: "maatir-haarir-bhat",
        name: "Maatir Haari'r Bhat",
        detail:
          "Clay pot rice · sealed since 4 PM · black sesame · dried river shrimp · golpata ash · a few drops of mustard-oil ghee · a tablespoon of begun pora alongside.",
        note:
          "The pot has been on the Unun since this afternoon. It comes to the table sealed. After the complexity of everything that preceded it, this course is the farm's most fundamental truth: rice, fire, time. The begun pora alongside is the same straw-charred aubergine from the field, here as a condiment, not a dish. Smoke cutting through smoke.",
      },
    ],
    coda:
      "The rice is not a course in the way the others are courses. It is what the farm is before it becomes everything else. It arrives now so you remember that before you eat the sweet.",
  },
  {
    id: "act-4",
    roman: "ACT IV",
    bengali: "মিষ্টি",
    subtitle: "The orchard and the delta's sweetness",
    heading: "To Close",
    lede:
      "The last act belongs to the phalbagan and the palm. What the two hundred trees offered today, and what the date palms gave this season.",
    dishes: [
      {
        slug: "phalbagan-thala",
        name: "Phalbagan Thala",
        detail:
          "Whatever the orchard held at its peak today · nothing added · described each evening when we see what is ready.",
        note:
          "In mango season: a single slice of the ripest Langra, cold. In winter: guava with a thread of nolen gur. In the dry months: amla with palm sugar and black salt. The orchard decides this course. We describe it each morning when we walk the trees.",
      },
      {
        slug: "payesh",
        name: "Payesh",
        detail:
          "Our own hand-pounded rice · village cow milk · cardamom · nothing else · served at near room temperature.",
        note:
          "This was ordered for you at the start of the evening. It has been cooking since. Served in an earthen bowl at near room temperature — not chilled, not warm — the way payesh was always served before refrigeration made the question possible. Three spoonfuls is enough. Four is the right number.",
      },
      {
        slug: "nolen-gur-kola-pata",
        name: "Nolen Gur, Kola Pata",
        detail:
          "Date-palm jaggery · set in banana leaf · crushed ice · fennel seed · served alongside a small earthen cup of gur chai.",
        note:
          "The last thing. The jaggery from the farm's own palms, set cold and served on a folded banana leaf over crushed ice. The gur chai alongside — dark tea with palm jaggery, no milk — is the closing drink. It is not available anywhere outside this delta. Drink it slowly.",
      },
    ],
  },
];

export default function Dining() {
  return (
    <div data-testid="page-dining">
      {/* HERO */}
      <section style={{ background: "var(--salt-white)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-14">
          <div
            className="italic-display mb-3"
            style={{ fontSize: "clamp(18px, 2vw, 24px)", color: "var(--sundari-gold)", opacity: 0.9 }}
            data-testid="page-bengali"
          >
            উনুন
          </div>
          <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>Dining · Unun</div>
          <h1 className="italic-display mt-6" style={{ fontSize: "clamp(48px, 7.5vw, 104px)", lineHeight: 1 }}>
            The menu is written
            <br />
            <span style={{ color: "var(--sundari-gold)" }}>each morning.</span>
          </h1>
          <p className="mt-8 max-w-[720px]" style={{ fontSize: "17px", lineHeight: 1.75 }}>
            Every dish at Unun begins at the farm gate at 5:30 AM. The farm head and the kitchen
            read the land together. What is ready comes in. What isn&rsquo;t stays.
          </p>
        </div>
      </section>

      {/* Menu geography */}
      <section style={{ background: "var(--bamboo-cream)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="eyebrow mb-8" style={{ color: "var(--delta-clay)" }}>Menu Geography</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ZONES.map((z) => (
              <article
                key={z.slug}
                className="hair-all rounded-[8px] overflow-hidden"
                style={{ background: "var(--salt-white)" }}
                data-testid={`menu-zone-${z.slug}`}
              >
                <div className={`block-placeholder ${z.tone}`} style={{ height: 150 }} />
                <div className="p-6">
                  <div className="label-track" style={{ color: "var(--sundari-gold)" }}>{z.bengali} · {z.name}</div>
                  <p className="mt-3" style={{ fontSize: "14px", lineHeight: 1.7 }}>{z.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Tasting menu masthead */}
      <section
        style={{ background: "var(--mangrove-night)", color: "var(--bamboo-cream)" }}
        data-testid="tasting-masthead"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <div className="eyebrow" style={{ color: "var(--sundari-gold)" }}>
            FarmStory Tasting Menu
          </div>
          <div
            className="italic-display mt-8"
            style={{ fontSize: "clamp(20px, 2.4vw, 30px)", opacity: 0.86 }}
          >
            মাটি ও নদীর গান
          </div>
          <h2
            className="italic-display mt-2"
            style={{ fontSize: "clamp(44px, 6vw, 80px)", lineHeight: 1.02 }}
          >
            The Farm &amp; the River&rsquo;s Song
          </h2>
          <div className="mx-auto my-10 divider-gold" />
          <div className="label-track" style={{ opacity: 0.75 }}>
            Twelve courses · Four acts · Served from 7:30 PM
          </div>

          <p
            className="italic-display mt-12 max-w-[720px] mx-auto"
            style={{ fontSize: "clamp(18px, 2vw, 22px)", opacity: 0.86, lineHeight: 1.5 }}
          >
            The menu follows the farm&rsquo;s morning and the river&rsquo;s tide. It is not the same
            menu every evening. The Ilish course appears only when the river offers it.
          </p>
        </div>
      </section>

      {/* Arc of the meal */}
      <section style={{ background: "var(--salt-white)" }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>The Arc of the Meal</div>
          <p
            className="italic-display mt-6 max-w-[820px]"
            style={{ fontSize: "clamp(22px, 2.6vw, 30px)", lineHeight: 1.4 }}
          >
            The meal begins at the farm&rsquo;s edge and moves toward the river. It returns to the
            hearth before it ends.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-8">
            {[
              { b: "প্রকৃতি", n: "Prakriti", t: "The farm wakes. Three small things from the morning." },
              { b: "নদী", n: "Nodi", t: "The river arrives. Seven courses, each a deeper engagement with the water." },
              { b: "আগুন", n: "Agun", t: "The hearth responds. What has been in clay since the afternoon." },
              { b: "মিষ্টি", n: "Misthi", t: "The sweet close. The orchard, the rice, and the last thing the farm offers." },
            ].map((a, i) => (
              <div key={i} className="hair-t pt-6">
                <div className="label-track" style={{ color: "var(--sundari-gold)" }}>0{i + 1}</div>
                <div className="italic-display mt-2" style={{ fontSize: "26px" }}>{a.b}</div>
                <div className="label-track mt-1" style={{ color: "var(--delta-clay)" }}>{a.n}</div>
                <p className="mt-3 text-[14px]" style={{ lineHeight: 1.65 }}>{a.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The full menu — Acts */}
      {ACTS.map((act, ai) => {
        const dark = ai % 2 === 1;
        return (
          <section
            key={act.id}
            data-testid={`act-${act.id}`}
            style={{
              background: dark ? "var(--mangrove-night)" : "var(--bamboo-cream)",
              color: dark ? "var(--bamboo-cream)" : "var(--mangrove-night)",
            }}
          >
            <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28">
              <div className="grid md:grid-cols-12 gap-10 md:gap-16">
                <header className="md:col-span-4">
                  <div className="label-track" style={{ color: dark ? "var(--sundari-gold)" : "var(--delta-clay)" }}>
                    {act.roman}
                  </div>
                  <div className="italic-display mt-4" style={{ fontSize: "clamp(28px, 3.4vw, 40px)", lineHeight: 1.05 }}>
                    {act.bengali}
                  </div>
                  <h3
                    className="italic-display mt-1"
                    style={{
                      fontSize: "clamp(28px, 3.6vw, 44px)",
                      lineHeight: 1.05,
                      color: dark ? "var(--sundari-gold)" : "var(--mangrove-night)",
                    }}
                  >
                    {act.heading}
                  </h3>
                  <div
                    className="label-track mt-3"
                    style={{ color: dark ? "rgba(242,233,212,0.66)" : "var(--delta-clay)" }}
                  >
                    {act.subtitle}
                  </div>
                  {act.lede && (
                    <p className="mt-6" style={{ fontSize: "14.5px", lineHeight: 1.75, opacity: dark ? 0.82 : 1 }}>
                      {act.lede}
                    </p>
                  )}
                  {act.label && (
                    <div
                      className="label-track mt-8"
                      style={{ color: dark ? "var(--sundari-gold)" : "var(--golpata-green)" }}
                    >
                      {act.label}
                    </div>
                  )}
                </header>

                <ul className="md:col-span-8 space-y-10">
                  {act.dishes.map((d) => (
                    <li
                      key={d.slug}
                      data-testid={`dish-${d.slug}`}
                      style={{
                        borderTop: dark
                          ? "1px solid rgba(250,247,240,0.14)"
                          : "1px solid rgba(26,42,30,0.14)",
                        paddingTop: 24,
                      }}
                    >
                      {d.pin && (
                        <div
                          className="label-track mb-3"
                          style={{ color: dark ? "var(--sundari-gold)" : "var(--dusk-coral)" }}
                        >
                          {d.pin}
                        </div>
                      )}
                      <h4
                        className="italic-display"
                        style={{ fontSize: "clamp(24px, 2.6vw, 32px)", lineHeight: 1.1 }}
                      >
                        {d.name}
                      </h4>
                      <p className="mt-3" style={{ fontSize: "15px", lineHeight: 1.7 }}>
                        {d.detail}
                      </p>
                      {d.note && (
                        <p
                          className="mt-4"
                          style={{
                            fontFamily: "var(--font-display)",
                            fontStyle: "italic",
                            fontSize: "15.5px",
                            lineHeight: 1.6,
                            opacity: dark ? 0.72 : 0.78,
                          }}
                        >
                          {d.note}
                        </p>
                      )}
                      {d.season && (
                        <div
                          className="label-track mt-4"
                          style={{
                            color: dark ? "var(--sundari-gold)" : "var(--dusk-coral)",
                            fontSize: "10.5px",
                            lineHeight: 1.6,
                          }}
                        >
                          {d.season}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {act.coda && (
                <p
                  className="italic-display mt-14 max-w-[720px] mx-auto text-center"
                  style={{ fontSize: "clamp(18px, 2vw, 22px)", lineHeight: 1.5, opacity: dark ? 0.75 : 0.85 }}
                  data-testid={`act-${act.id}-coda`}
                >
                  {act.coda}
                </p>
              )}
            </div>
          </section>
        );
      })}

      {/* Closing note + CTA */}
      <section style={{ background: "var(--salt-white)" }}>
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <p
            className="italic-display"
            style={{ fontSize: "clamp(20px, 2.2vw, 26px)", lineHeight: 1.55, opacity: 0.85 }}
          >
            When the Ilish is not in the river the course does not appear. We wait.
          </p>
          <a
            href={waLink("Hello FarmStory, I'd like to ask about tonight's menu.")}
            target="_blank"
            rel="noreferrer"
            className="btn-pill btn-pill--dark mt-10"
            data-testid="dining-cta-whatsapp"
          >
            Reserve a Table on WhatsApp
          </a>
          <div className="mt-5 flex justify-center">
            <SaveNumberLink tone="dark" testId="dining-save-number" />
          </div>
        </div>
      </section>
    </div>
  );
}
